import { IntegrationSource } from '@/constants';
import { MockRoute } from '../mockBackend';
import { PreviewDataset, PreviewEmployee, PreviewTraining } from '../../preview.types';

// Serve layer for the Targeted Training Planning preview: the "Manage Training" area
// (/settings/learning). Reuses the SAME synthetic aviation fixture as the Team Training tab — the
// courses here are the dataset's `trainings`, so a prospect sees one consistent catalogue across
// the demo. Enrolment counts are COMPUTED from competency holdings (no separate enrolment data),
// keeping the fixture small and self-consistent.
//
// Read-only + fail-closed: courses carry a non-Manual integrationSource so row-click navigation and
// the row action menu stay inert (no course-detail / mutation endpoints to mock). All GET-only.

// "Today" for expiry classification. The dataset embeds fixed ISO expiry dates; this is the matching
// fixed reference so the demo is deterministic (and Date.now() is unavailable in some contexts).
const TODAY = '2026-06-26';

type CourseStatus = 'completed' | 'overdue' | 'inProgress' | 'notStarted';

/** Per (employee, course) status — null when the course is not relevant to the employee's job. */
function statusFor(
  employee: PreviewEmployee,
  training: PreviewTraining,
  jobRequired: Set<string>
): CourseStatus | null {
  const compIds = training.competencyIds;
  if (!compIds.some((c) => jobRequired.has(c))) return null; // not part of this role's training

  const held = employee.attributes.filter((a) => compIds.includes(a.attributeDefinitionId));
  if (held.length === 0) return 'notStarted';
  if (held.length < compIds.length) return 'inProgress';
  // Holds every competency the course delivers → completed, unless a certification has lapsed.
  const expired = held.some((a) => a.expiresOn && a.expiresOn < TODAY);
  return expired ? 'overdue' : 'completed';
}

interface StatusCounts {
  enrolled: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  failed: number;
  overdue: number;
  pending: number;
}

const emptyCounts = (): StatusCounts => ({
  enrolled: 0,
  completed: 0,
  inProgress: 0,
  notStarted: 0,
  failed: 0,
  overdue: 0,
  pending: 0,
});

function add(counts: StatusCounts, status: CourseStatus): void {
  counts.enrolled += 1;
  counts[status] += 1;
}

function jobRequiredSet(dataset: PreviewDataset, jobId: string): Set<string> {
  return new Set(dataset.jobs.find((j) => j.id === jobId)?.requiredAttributeIds ?? []);
}

function attrName(dataset: PreviewDataset, id: string): string {
  return dataset.attributeDefinitions.find((a) => a.id === id)?.name ?? id;
}

// A competency "has an expiry date" if any holder carries one (drives the recurrence/expiry hints).
function attrHasExpiry(dataset: PreviewDataset, id: string): boolean {
  return dataset.employees.some((e) =>
    e.attributes.some((a) => a.attributeDefinitionId === id && !!a.expiresOn)
  );
}

const PROVIDER = {
  name: 'LearnUpon',
  integrationId: 'lu',
  integrationSource: IntegrationSource.LearnUpon,
};
const FACULTY = { id: 'f-flight-ops', name: 'Flight Operations' };
const DURATIONS = ['1h 30m', '2h', '45m', '3h'];

function buildCourse(dataset: PreviewDataset, training: PreviewTraining, index: number) {
  const counts = emptyCounts();
  dataset.employees.forEach((e) => {
    const status = statusFor(e, training, jobRequiredSet(dataset, e.jobId ?? ''));
    if (status) add(counts, status);
  });

  const associatedAttributeDefinitions = training.competencyIds.map((id) => ({
    attributeDefinitionId: id,
    name: attrName(dataset, id),
    hasExpiryDate: attrHasExpiry(dataset, id),
  }));

  return {
    id: training.id,
    sku: training.id.toUpperCase(),
    title: training.name,
    description: `${training.name} — delivered via ${PROVIDER.name}.`,
    imageUrl: null, // placeholder card art only — never request a real image (fail-closed)
    level: 'Intermediate',
    courseType: 'Course',
    price: 0,
    currency: 'NZD',
    averageRating: 4 + (index % 2),
    numberOfReviews: 8 + index * 3,
    numberOfModules: training.competencyIds.length + 2,
    duration: DURATIONS[index % DURATIONS.length],
    courseUrl: null,
    sourceId: null,
    associatedAttributeDefinitions,
    numberOfAssociatedAttributes: associatedAttributeDefinitions.length,
    competenciesLocked: false,
    faculties: [FACULTY],
    languages: ['en'],
    ...counts,
    recurrence: 0,
    integrationSource: PROVIDER.integrationSource, // non-Manual → row stays inert (read-only)
    facultiesAsString: FACULTY.name,
    integrationId: PROVIDER.integrationId,
    integrationName: PROVIDER.name,
    isLearningPath: false,
  };
}

function trainings(dataset: PreviewDataset): PreviewTraining[] {
  return dataset.trainings ?? [];
}

// Wraps a flat list in the PaginatedResponse envelope (single page — the demo dataset is small).
function paginated<T>(data: T[]) {
  return {
    currentPage: 1,
    totalPages: 1,
    totalCount: data.length,
    hasNext: false,
    hasPrevious: false,
    data,
  };
}

function buildCourses(dataset: PreviewDataset) {
  return { courses: paginated(trainings(dataset).map((t, i) => buildCourse(dataset, t, i))) };
}

function buildCourseFilters() {
  return {
    filters: {
      providers: [PROVIDER],
      faculties: [FACULTY],
      languages: ['en'],
      courseTypes: ['Course'],
      regions: ['New Zealand'],
      minPriceInCents: 0,
      maxPriceInCents: 0,
    },
  };
}

function studentCounts(dataset: PreviewDataset, employee: PreviewEmployee): StatusCounts {
  const counts = emptyCounts();
  const jobRequired = jobRequiredSet(dataset, employee.jobId ?? '');
  trainings(dataset).forEach((t) => {
    const status = statusFor(employee, t, jobRequired);
    if (status) add(counts, status);
  });
  return counts;
}

function buildStudents(dataset: PreviewDataset) {
  const jobs = new Map(dataset.jobs.map((j) => [j.id, j]));
  const data = dataset.employees.map((e) => ({
    employeeId: e.id,
    firstName: e.firstName,
    lastName: e.lastName,
    preferredName: e.firstName,
    fullName: `${e.firstName} ${e.lastName}`,
    jobTitle: jobs.get(e.jobId ?? '')?.title ?? '',
    hasProfilePicture: false, // initials only — never request a real avatar (fail-closed)
    thumbnailUrl: null,
    ...studentCounts(dataset, e),
    learnUponEnrollmentId: '',
    courseEnrollmentId: '',
    canUnenrol: false,
  }));
  return { students: paginated(data) };
}

function buildStudentSummary(dataset: PreviewDataset) {
  const summary = { completed: 0, inProgress: 0, notStarted: 0, overdue: 0, failed: 0, pending: 0 };
  dataset.employees.forEach((e) => {
    const c = studentCounts(dataset, e);
    summary.completed += c.completed;
    summary.inProgress += c.inProgress;
    summary.notStarted += c.notStarted;
    summary.overdue += c.overdue;
    summary.failed += c.failed;
    summary.pending += c.pending;
  });
  return { summary };
}

// The Students tab mounts TrainingStudentFilterDrawer, which fetches this on mount (even closed).
// Without it the call would fail closed and leave the Faculty/Course dropdowns permanently empty.
function buildStudentFilters(dataset: PreviewDataset) {
  return {
    faculties: [{ id: FACULTY.id, name: FACULTY.name }],
    courses: trainings(dataset).map((t) => ({ id: t.id, name: t.name })),
  };
}

// One synthetic prepaid balance so the Training Manager balance widget shows a sane figure.
function buildBalances() {
  return {
    balances: [
      {
        prepaidBalanceId: 'bal-1',
        name: 'Training Credit',
        currentAmountInCents: 500000,
        currency: 'NZD',
        isActive: true,
        restrictedCourseIds: [],
        isUnlimitedCredit: false,
      },
    ],
  };
}

export const trainingRoutes: MockRoute[] = [
  { pattern: '/TrainingManager/balances', build: () => buildBalances() },
  { pattern: '/TrainingManager/courses/filters', build: () => buildCourseFilters() },
  { pattern: '/TrainingManager/courses', build: ({ dataset }) => buildCourses(dataset) },
  {
    pattern: '/TrainingManager/students/summary',
    build: ({ dataset }) => buildStudentSummary(dataset),
  },
  {
    pattern: '/TrainingManager/students/filters',
    build: ({ dataset }) => buildStudentFilters(dataset),
  },
  { pattern: '/TrainingManager/students', build: ({ dataset }) => buildStudents(dataset) },
];
